import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, Bell } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isPublicUser, setIsPublicUser] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    // Check all token types — user, staff, admin
    const userToken = localStorage.getItem('user_token');
    const staffToken = localStorage.getItem('staff_token');
    const user = localStorage.getItem('user_data');
    const adminUser = localStorage.getItem('admin_user');
    if (userToken && user) {
      setIsLoggedIn(true);
      setIsPublicUser(true);
      try { setUserData(JSON.parse(user)); } catch { setUserData(null); }
      // Fetch fresh profile (avatar_url) and unread count
      axios.get(`${BACKEND_URL}/api/users/me`, { headers: { Authorization: `Bearer ${userToken}` } })
        .then(r => {
          setUserData(prev => ({ ...(prev || {}), ...r.data }));
          try { localStorage.setItem('user_data', JSON.stringify({ ...(JSON.parse(localStorage.getItem('user_data') || '{}')), ...r.data })); } catch {}
        }).catch(() => {});
      const loadUnread = () => {
        axios.get(`${BACKEND_URL}/api/users/notifications?limit=1`, { headers: { Authorization: `Bearer ${userToken}` } })
          .then(r => setUnreadCount(r.data?.unread_count || 0)).catch(() => {});
      };
      loadUnread();
      const iv = setInterval(loadUnread, 45000);
      return () => { window.removeEventListener('scroll', handleScroll); clearInterval(iv); };
    } else if (staffToken) {
      // Staff user browsing public pages
      setIsLoggedIn(true);
      if (adminUser) {
        try { setUserData(JSON.parse(adminUser)); } catch { setUserData({ first_name: 'Staff' }); }
      } else {
        setUserData({ first_name: 'Staff' });
      }
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('staff_token');
    localStorage.removeItem('admin_user');
    setIsLoggedIn(false);
    setUserData(null);
    navigate('/');
    window.location.reload();
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Team', path: '/team' },
    { name: 'Affiliates', path: '/partners' },
    { name: 'Vacancies', path: '/vacancies' },
    { name: 'News', path: '/news' }
  ];

  return (
    <header data-testid="site-header"
      style={{ top: 'var(--alert-banner-height, 0px)' }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#05050A]/80 backdrop-blur-xl border-b border-white/[0.04]' : 'bg-transparent'
      }`}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-4">
          <Link to="/" className="flex items-center group">
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_209c8813-566e-47da-85c4-1d1fadce8833/artifacts/rzf7k7uf_starnight101.png"
              alt="Starnight Holdings"
              className="h-10 w-auto object-contain group-hover:opacity-80 transition-opacity"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className={`relative px-4 py-2 text-xs font-medium tracking-wide transition-all rounded-lg ${
                  location.pathname === link.path
                    ? 'text-[#00E5FF]'
                    : 'text-white/40 hover:text-white/70'
                }`}>
                {link.name}
                {location.pathname === link.path && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-px bg-[#00E5FF]" />
                )}
              </Link>
            ))}

            <div className="w-px h-5 bg-white/[0.06] mx-2" />

            {isLoggedIn ? (
              <div className="flex items-center gap-2 ml-1">
                {isPublicUser && (
                  <button onClick={() => navigate('/account/settings?tab=inbox')} data-testid="header-inbox-bell"
                    className="relative p-2 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/[0.04] transition-all" title="Inbox">
                    <Bell className="w-4 h-4" />
                    {unreadCount > 0 && (
                      <span data-testid="header-inbox-unread"
                        className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#00E5FF] text-black text-[9px] font-bold flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                )}
                <button onClick={() => navigate('/account/settings')} data-testid="header-account-btn"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] transition-all">
                  {userData?.avatar_url ? (
                    <img src={`${BACKEND_URL}${userData.avatar_url}`} alt="avatar"
                      className="w-6 h-6 rounded-md object-cover" />
                  ) : (
                    <div className="w-6 h-6 bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] rounded-md flex items-center justify-center">
                      <span className="text-white text-[10px] font-bold">{(userData?.first_name || userData?.display_name || 'U').charAt(0)}</span>
                    </div>
                  )}
                  <span className="text-white/50 text-xs">{userData?.first_name || userData?.display_name}</span>
                </button>
                <button onClick={handleLogout}
                  className="p-2 rounded-lg text-white/20 hover:text-white/50 hover:bg-white/[0.03] transition-all" title="Logout">
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 ml-1">
                <Button onClick={() => navigate('/login')} size="sm"
                  className="bg-transparent text-white/40 hover:text-white/70 hover:bg-white/[0.03] text-xs font-medium px-4 py-2 h-auto rounded-lg transition-all">
                  Login
                </Button>
                <Button onClick={() => navigate('/signup')} size="sm"
                  className="bg-white text-black hover:bg-white/90 text-xs font-medium px-4 py-2 h-auto rounded-lg transition-all">
                  Sign Up
                </Button>
              </div>
            )}
          </nav>

          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white/50 hover:text-white/80 transition-colors p-2">
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#05050A]/95 backdrop-blur-xl border-t border-white/[0.04]">
          <div className="px-6 py-5 space-y-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  location.pathname === link.path
                    ? 'text-[#00E5FF] bg-[#00E5FF]/[0.05]'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/[0.02]'
                }`}>
                {link.name}
              </Link>
            ))}
            {!isLoggedIn && (
              <div className="flex gap-2 pt-3 border-t border-white/[0.04] mt-3">
                <Button onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }}
                  className="flex-1 bg-white/[0.03] border border-white/[0.06] text-white/50 hover:text-white text-xs rounded-lg">Login</Button>
                <Button onClick={() => { navigate('/signup'); setIsMobileMenuOpen(false); }}
                  className="flex-1 bg-white text-black hover:bg-white/90 text-xs rounded-lg">Sign Up</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
